import { Component, Inject, OnInit } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { forkJoin } from 'rxjs';
import { MatConfirmDialogComponent } from 'src/app/shared/mat-confirm-dialog/mat-confirm-dialog.component';
import { ICategorie } from 'src/app/shared/models/categorie';
import { ICompetence } from 'src/app/shared/models/competence';
import { IUniteOrganisationnelle } from 'src/app/shared/models/up-evaluation';

import { SnackbarService } from 'src/app/shared/services/snackbar.service';
import { CategorieService } from '../../categorie-list/categorie-service';
import { UniteOrganisationnelleService } from '../../uo-evaluation/uo-evaluation.service';
import { CompetenceService } from '../competence.service';

@Component({
  selector: 'app-competence-dialog',
  templateUrl: './competence-dialog.component.html',
  styleUrls: ['./competence-dialog.component.less']
})
export class CompetenceDialogComponent implements OnInit {

  form!: UntypedFormGroup;
  isEditMode: boolean;
  isSubmitting = false;
  backendError: string | null = null;

  categories: ICategorie[] = [];
  unitesOrganisationnelles: IUniteOrganisationnelle[] = [];

  constructor(
    private _fb: UntypedFormBuilder,
    private competenceService: CompetenceService,
    private categorieService: CategorieService,
    private uoService: UniteOrganisationnelleService,
    private dialog: MatDialog,
    private snackbarService: SnackbarService,
    public dialogRef: MatDialogRef<CompetenceDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: ICompetence | null
  ) {
    this.isEditMode = data != null;
  }

  // ── Lifecycle ─────────────────────────────────────────────────────────────

  ngOnInit(): void {
    const uoIds = this.data?.unitesOrganisationnelles?.map(uo => uo.id) ?? [];

    this.form = this._fb.group({
      libelle:                    [this.data?.libelle ?? '',       [Validators.required, Validators.maxLength(255)]],
      categorieId:                [this.data?.categorieId ?? null, Validators.required],
      unitesOrganisationnellesIds:[uoIds],
      active:                     [this.data?.active ?? true]
    });

    this.loadReferenceData();
  }

  // ── Chargement des référentiels ───────────────────────────────────────────

  loadReferenceData(): void {
    forkJoin({
      categories: this.categorieService.getAll(),
      uos: this.uoService.getAll()
    }).subscribe({
      next: ({ categories, uos }) => {
        this.categories = categories;
        this.unitesOrganisationnelles = uos;
      },
      error: (err) => {
        this.backendError = this.extractErrorMessage(err, 'Erreur lors du chargement des référentiels');
      }
    });
  }

  // ── Soumission ────────────────────────────────────────────────────────────

  onSubmit(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    this.backendError = null;
    this.isSubmitting = true;

    const { libelle, categorieId, unitesOrganisationnellesIds, active } = this.form.value;

    const action$ = this.isEditMode
      ? this.competenceService.update(this.data!.id, { libelle, categorieId, active })
      : this.competenceService.create({ libelle, categorieId, unitesOrganisationnellesIds, active });

    const successMsg = this.isEditMode ? 'Compétence modifiée avec succès' : 'Compétence créée avec succès';
    const fallbackMsg = this.isEditMode ? 'Erreur lors de la modification' : 'Erreur lors de la création';

    action$.subscribe({
      next: (result) => {
        if (this.isEditMode) {
          this.syncUos(result.id, unitesOrganisationnellesIds, successMsg);
        } else {
          this.isSubmitting = false;
          this.snackbarService.openSnackBar(successMsg);
          this.dialogRef.close(result);
        }
      },
      error: (err) => {
        this.isSubmitting = false;
        this.backendError = this.extractErrorMessage(err, fallbackMsg);
      }
    });
  }

  /**
   * Synchronise les UOs après un PATCH :
   * - Supprime les associations qui ne sont plus dans la liste
   * - Ajoute les nouvelles associations
   */
  private syncUos(competenceId: string, newUoIds: string[], successMsg: string): void {
    const currentUoIds = this.data!.unitesOrganisationnelles?.map(uo => uo.id) ?? [];

    const toAdd    = newUoIds.filter(id => !currentUoIds.includes(id));
    const toRemove = this.data!.unitesOrganisationnelles?.filter(uo => !newUoIds.includes(uo.id)) ?? [];

    const addRequests = toAdd.map(uoId =>
      this.competenceService.addUoCompetence({ uniteOrganisationnelleId: uoId, competenceId })
    );
    const removeRequests = toRemove.map(uo =>
      this.competenceService.removeUoCompetence(uo.uoCompetenceId!)
    );

    const allRequests = [...addRequests, ...removeRequests];

    if (allRequests.length === 0) {
      this.isSubmitting = false;
      this.snackbarService.openSnackBar(successMsg);
      this.dialogRef.close(true);
      return;
    }

    forkJoin(allRequests).subscribe({
      next: () => {
        this.isSubmitting = false;
        this.snackbarService.openSnackBar(successMsg);
        this.dialogRef.close(true);
      },
      error: (err) => {
        this.isSubmitting = false;
        this.backendError = this.extractErrorMessage(err, 'Erreur lors de la mise à jour des UO');
      }
    });
  }

  // ── Suppression ───────────────────────────────────────────────────────────

  onDelete(): void {
    const confirmRef = this.dialog.open(MatConfirmDialogComponent, {
      width: '400px',
      data: { message: `Supprimer la compétence "${this.data!.libelle}" ?` }
    });

    confirmRef.afterClosed().subscribe(confirmed => {
      if (!confirmed) return;
      this.isSubmitting = true;
      this.competenceService.delete(this.data!.id).subscribe({
        next: () => {
          this.isSubmitting = false;
          this.snackbarService.openSnackBar('Compétence supprimée avec succès');
          this.dialogRef.close('deleted');
        },
        error: (err) => {
          this.isSubmitting = false;
          this.backendError = this.extractErrorMessage(err, 'Erreur lors de la suppression');
        }
      });
    });
  }

  onCancel(): void {
    this.dialogRef.close();
  }

  // ── Helper erreurs ────────────────────────────────────────────────────────

  private extractErrorMessage(err: any, fallback: string): string {
    const body = err?.error;
    if (body?.message && typeof body.message === 'string') return body.message;
    if (typeof body === 'string') return body;
    return fallback;
  }
}